// Admin licenses (/admin/licenses) — cross-user list with state/tier filters and
// revoke/unbind actions. One-time model: validity = active + signature + machine
// binding; there is NO expiry column and none may be added.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { FilterSelect } from "@/components/FilterSelect";
import { LicenseAdminActions } from "@/components/LicenseAdminActions";
import { MachineBinding } from "@/components/MachineBinding";
import { useAsync } from "@/lib/useAsync";
import { listAdminLicenses } from "@/lib/admin";
import {
  formatDate,
  licenseStateLabel,
  licenseStateOptions,
  licenseStateTone,
  tierLabel,
  tierOptions,
} from "@/lib/format";
import type { AdminLicense, LicenseState, Paged, TierId } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, FilterBar, type DataColumn } from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";
import { BreakableText } from "@/components/admin/BreakableText";

const PAGE = 25;

const STATE_OPTIONS = licenseStateOptions();
const TIER_OPTIONS = tierOptions({ withAll: true });

const COLUMNS: readonly DataColumn[] = [
  { label: "Terbit" },
  { label: "Pengguna" },
  { label: "Tier" },
  { label: "Status" },
  { label: "Perangkat" },
  { label: "Aksi" },
];

export function AdminLicenses() {
  const [state, setState] = useState("");
  const [tier, setTier] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminLicense>>(
    (s) =>
      listAdminLicenses(
        { state: state as LicenseState | "", tier: tier as TierId | "", limit: PAGE, offset },
        s,
      ),
    [state, tier, offset],
  );

  // After a row-count shrink (revoke moves a row out of the active filter), a
  // stale offset can land past the end → an empty page with the pagination
  // control hidden. Step back to the last non-empty page.
  useEffect(() => {
    if (data && data.total > 0 && offset >= data.total) {
      setOffset(Math.max(0, (Math.ceil(data.total / PAGE) - 1) * PAGE));
    }
  }, [data, offset]);

  if (loading && !data) return <Loading label="Memuat lisensi…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Lisensi"
        lede="Semua lisensi lintas pengguna. Lisensi aktif sampai dicabut — tanpa masa berlaku."
      />

      <FilterBar label="Filter lisensi">
        <FilterSelect
          label="Status"
          value={state}
          options={STATE_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setState(v);
          }}
        />
        <FilterSelect
          label="Tier"
          value={tier}
          options={TIER_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setTier(v);
          }}
        />
      </FilterBar>

      {!data || data.items.length === 0 ? (
        <EmptyState title="Tidak ada lisensi">Tidak ada lisensi untuk filter ini.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable label="Daftar lisensi" columns={COLUMNS} stackBelow="58rem" busy={loading}>
              {data.items.map((l) => (
                <DataRow key={l.id}>
                  <DataCell label="Terbit" className={dataCellClass}>
                    {formatDate(l.issued_at)}
                  </DataCell>
                  {/* An email breaks only before its "@" when the table is tight. */}
                  <DataCell label="Pengguna">
                    <Link to={`/admin/users/${l.user_id}`}>
                      <BreakableText text={l.user_email ?? l.user_id} at={l.user_email ? "email" : "id"} />
                    </Link>
                  </DataCell>
                  <DataCell label="Tier">{tierLabel(l.tier)}</DataCell>
                  <DataCell label="Status">
                    <Badge tone={licenseStateTone(l.revoked_at)} dot>
                      {licenseStateLabel(l.revoked_at)}
                    </Badge>
                  </DataCell>
                  {/* A 32-character machine id breaks anywhere; the floor keeps it
                      to two readable lines instead of a one-character column. */}
                  <DataCell label="Perangkat" className="min-w-48">
                    <MachineBinding machineId={l.machine_id} />
                  </DataCell>
                  <DataCell actions>
                    <LicenseAdminActions license={l} onDone={reload} />
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
